'use client';

import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import type { IngestionPipeline } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { dayjs } from '@/utils/dayjs';

import { DeleteIngestionPipelineDialog } from './deleteIngestionPipelineDialog';
import { IngestionPipelineStatusCell } from './ingestionPipelineStatusCell';

interface IngestionPipelinePanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const IngestionPipelinePanel = ({
  id,
  workspaceId,
  onClose,
  onRefresh,
}: IngestionPipelinePanelProps) => {
  const dal = useDAL();
  const { isAuthorized } = useRoles();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: pipelinesData, isLoading: isPipelinesLoading } =
    dal.lakehouse.useListIngestionPipelines(workspaceId);

  const pipeline = pipelinesData?.ingestionPipelines?.find(
    (p: IngestionPipeline) => p.id === id
  );

  if (isPipelinesLoading || !pipeline) {
    return (
      <PanelWrapper
        header={{
          title: 'Ingestion Pipeline Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const canUpdate = isAuthorized('update:ingestion_pipeline');
  const canDelete = isAuthorized('delete:ingestion_pipeline');

  const isEnabled = pipeline.status === 'ENABLED';
  const isDisabled = pipeline.status === 'DISABLED';

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      await dal.lakehouse.enableIngestionPipeline(workspaceId, pipeline.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to enable pipeline:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    setIsLoading(true);
    try {
      await dal.lakehouse.disableIngestionPipeline(workspaceId, pipeline.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to disable pipeline:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PanelWrapper
        header={{
          title: 'Ingestion Pipeline Details',
          onClose,
          actions: isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <OptionMenu
              options={[
                {
                  id: 'enable-pipeline',
                  text: 'Enable',
                  icon: 'material-symbols-play-arrow-rounded',
                  menuItemProps: {
                    disabled: !canUpdate || isEnabled,
                    onClick: () => void handleEnable(),
                  },
                },
                {
                  id: 'disable-pipeline',
                  text: 'Disable',
                  icon: 'material-symbols-pause-rounded',
                  menuItemProps: {
                    disabled: !canUpdate || isDisabled,
                    onClick: () => void handleDisable(),
                  },
                },
                {
                  id: 'divider',
                  divider: true,
                },
                {
                  id: 'delete-pipeline',
                  text: 'Delete',
                  icon: 'material-symbols-delete-outline-rounded',
                  menuItemProps: {
                    disabled: !canDelete,
                    onClick: () => setDeleteDialogOpen(true),
                  },
                },
              ]}
            />
          ),
        }}
      >
        <Stack gap='24px' padding='24px 40px'>
          {/* Pipeline Details */}
          <Stack gap='12px'>
            <DetailRow label='ID' value={pipeline.id} />
            <DetailRow label='Name' value={pipeline.name} />
            <DetailRow label='Source Type' value={pipeline.sourceType} />
            <DetailRow
              label='Status'
              value={<IngestionPipelineStatusCell status={pipeline.status} />}
            />
            <DetailRow
              label='Created'
              value={dayjs
                .utc(pipeline.createdAt)
                .format('MMM D, YYYY HH:mm:ss')}
            />
          </Stack>

          {/* S3 Source Configuration */}
          {pipeline.s3SourceProperties && (
            <>
              <Divider />
              <Stack gap='12px'>
                <Typography variant='subtitle2' color='text.secondary'>
                  S3 Source Configuration
                </Typography>
                <DetailRow
                  label='Bucket'
                  value={pipeline.s3SourceProperties.bucket}
                />
                <DetailRow
                  label='Prefix'
                  value={pipeline.s3SourceProperties.prefix}
                />
                <DetailRow
                  label='Region'
                  value={pipeline.s3SourceProperties.region}
                />
                <DetailRow
                  label='Account ID'
                  value={pipeline.s3SourceProperties.ownerAccountId}
                />
              </Stack>
            </>
          )}
        </Stack>
      </PanelWrapper>

      <DeleteIngestionPipelineDialog
        pipeline={pipeline}
        workspaceId={workspaceId}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          onRefresh();
          onClose();
        }}
      />
    </>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '100px' }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant='body2' sx={{ wordBreak: 'break-word', flex: 1 }}>
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};
